import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider,
    IconButton, Tooltip, Typography
} from '@mui/material';
import {
    ArrowBack, CheckCircle, HourglassEmpty,
    PlayArrow, RadioButtonUnchecked, Send, Timer, Undo
} from '@mui/icons-material';
import { useKDSExpedicao } from '../../hooks/useKDSExpedicao';
import { useEstacoesKDS } from '../../hooks/useEstacoesKDS';
import { IItemPedidoKDS, IPedidoKDSExpedicao, StatusItemKDS, StatusKDS } from '../../interfaces/IKDS';
import { KDSTimer } from '../../components/KDSTimer';
import { useKdsTokens } from '../../theme/kdsTokens';
import { useKDSKeyboardNavigation } from '../../hooks/useKDSKeyboardNavigation';

const calcularStatusKDS = (itens: IItemPedidoKDS[]): StatusKDS => {
    const ativos = itens.filter(i => i.status !== StatusItemKDS.CANCELADO && i.status !== StatusItemKDS.LIBERADO);
    if (!ativos.length) return StatusKDS.PARADO;
    if (ativos.every(i => i.status === StatusItemKDS.PRONTO)) return StatusKDS.CONCLUIDO;
    if (ativos.some(i => i.status === StatusItemKDS.EM_PREPARO)) return StatusKDS.EM_ANDAMENTO;
    return StatusKDS.PARADO;
};

const IconeStatus: React.FC<{ status: StatusItemKDS; size?: number }> = ({ status, size = 18 }) => {
    const tokens = useKdsTokens();
    const sx = { fontSize: size, color: tokens.statusCor(status) };
    if (status === StatusItemKDS.PRONTO) return <CheckCircle sx={sx} />;
    if (status === StatusItemKDS.EM_PREPARO) return <HourglassEmpty sx={sx} />;
    if (status === StatusItemKDS.AGUARDANDO_DISPARO) return <Timer sx={sx} />;
    return <RadioButtonUnchecked sx={sx} />;
};

const formatMin = (s?: number) => s ? `${Math.round(s / 60)} min` : null;

const labelTipoDetalhe = (tipo: string): string | null => {
    if (tipo === 'borda') return 'Borda';
    if (tipo === 'massa') return 'Massa';
    if (tipo === 'sabor') return 'Sabor';
    return null; // 'item' já tem "Adc:" no próprio descricao
};

const formatarDetalhe = (detalhe: { tipo: string; descricao: string }): string => {
    const label = labelTipoDetalhe(detalhe.tipo);
    return label ? `${label}: ${detalhe.descricao}` : detalhe.descricao;
};

export const PainelExpedicao: React.FC = () => {
    const tokens = useKdsTokens();
    const { pedidos, loading, liberarPedido, dispararPedido, dispararItemIndividual, reverterItem } = useKDSExpedicao();
    const { estacoes } = useEstacoesKDS();
    const navigate = useNavigate();

    useEffect(() => { document.title = 'Expedição'; }, []);

    const nomeEstacao = (uuid: string) => estacoes.find(e => e.uuid === uuid)?.nome ?? uuid.slice(-4);
    const corEstacao = (uuid: string) => estacoes.find(e => e.uuid === uuid)?.cor ?? tokens.textoFraco;

    const porOrdemChegada = (lista: IPedidoKDSExpedicao[]) =>
        [...lista].sort((a, b) =>
            a.criadoEm && b.criadoEm
                ? new Date(a.criadoEm).getTime() - new Date(b.criadoEm).getTime()
                : 0
        );

    const aguardandoDisparo = porOrdemChegada(pedidos.filter(p => p.temDisparo));
    const emAndamento = porOrdemChegada(pedidos.filter(p => !p.temDisparo && !p.emDisparo && !p.todosProntos));
    const emDisparo = porOrdemChegada(pedidos.filter(p => p.emDisparo && !p.temDisparo && !p.todosProntos));
    const prontos = porOrdemChegada(pedidos.filter(p => p.todosProntos));

    // Navegação por teclado — lista flat: aguardandoDisparo + emDisparo + prontos + emAndamento
    const navItems = React.useMemo(() => {
        const todos = [...aguardandoDisparo, ...emDisparo, ...prontos, ...emAndamento];
        return todos.map(p => ({
            pedidoUuid: p.pedidoUuid,
            itens: p.itens.filter(i => i.status !== StatusItemKDS.CANCELADO && i.status !== StatusItemKDS.LIBERADO && i.status !== StatusItemKDS.AGUARDANDO_DISPARO),
        })).filter(p => p.itens.length > 0);
    }, [aguardandoDisparo, emDisparo, prontos, emAndamento]);

    const { selectedPedidoUuid } = useKDSKeyboardNavigation(navItems, {
        onIniciarItem: () => {},
        onConcluirItem: () => {},
        onReverterItem: reverterItem,
        onLiberarPedido: liberarPedido,
    });

    const renderGrupoDisparo = (itens: IItemPedidoKDS[]) => {
        const itemPrincipal = itens.find(i => i.itemTipo === 'produto') ?? itens[0];
        const subItens = itens.filter(i => i !== itemPrincipal);
        const todoDisparado = itens.every(i => i.status !== StatusItemKDS.AGUARDANDO_DISPARO || !!i.disparadoEm);
        const observacoes = itemPrincipal.observacoes ?? itens.find(i => i.observacoes)?.observacoes;

        return (
            <Box key={itemPrincipal.produtoUuid + itemPrincipal.uuid} sx={{
                borderRadius: 1,
                background: tokens.tenue(todoDisparado ? tokens.palette.success.main : tokens.palette.warning.main, 0.1),
                border: `1px solid ${tokens.tenue(todoDisparado ? tokens.palette.success.main : tokens.palette.warning.main, 0.5)}`,
                overflow: 'hidden',
            }}>
                {/* Cabeçalho do produto */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontSize="0.875rem" fontWeight={700} noWrap sx={{ color: tokens.texto }}>
                            {itemPrincipal.quantidade}x {itemPrincipal.produtoDescricao}
                        </Typography>
                        {observacoes && (
                            <Typography sx={{ color: tokens.palette.warning.main, fontWeight: 600 }} fontSize="0.72rem" noWrap>
                                ⚠ {observacoes}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Sub-itens KDS com estação própria */}
                {subItens.length > 0 && (
                    <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                        {subItens.map(sub => {
                            const jaDisparado = sub.status !== StatusItemKDS.AGUARDANDO_DISPARO || !!sub.disparadoEm;
                            return (
                                <Box key={sub.uuid} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                                    <Chip
                                        label={nomeEstacao(sub.estacaoKdsUuid)}
                                        size="small"
                                        sx={{ background: corEstacao(sub.estacaoKdsUuid), color: 'white', fontSize: '0.65rem', height: 18, flexShrink: 0 }}
                                    />
                                    <Typography fontSize="0.8rem" sx={{ flex: 1, color: tokens.textoSecundario }} noWrap>
                                        ↳ {sub.itemDescricao ? formatarDetalhe({ tipo: sub.itemTipo ?? 'item', descricao: sub.itemDescricao }) : sub.produtoDescricao}
                                    </Typography>
                                    {!jaDisparado ? (
                                        <Button variant="contained" color="warning" size="small"
                                            startIcon={<PlayArrow sx={{ fontSize: 12 }} />}
                                            onClick={() => dispararItemIndividual(sub.uuid)}
                                            sx={{
                                                textTransform: 'none', fontWeight: 'bold', fontSize: '0.7rem',
                                                py: 0.25, px: 0.75, flexShrink: 0,
                                            }}
                                        >
                                            Disparar
                                        </Button>
                                    ) : (
                                        <Chip
                                            label="Disparado"
                                            size="small"
                                            icon={<CheckCircle sx={{ fontSize: '12px !important', color: `${tokens.palette.success.main} !important` }} />}
                                            sx={{ background: tokens.tenue(tokens.palette.success.main, 0.14), color: tokens.palette.success.main, fontSize: '0.65rem', height: 18, flexShrink: 0 }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Detalhes estruturados quando não há sub-itens KDS (borda/massa/sabor sem estação própria) */}
                {subItens.length === 0 && itemPrincipal.detalhesProduto && itemPrincipal.detalhesProduto.length > 0 && (
                    <Box sx={{ px: 2, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {itemPrincipal.detalhesProduto.map((detalhe, i) => (
                            <Typography key={i} fontSize="0.78rem" noWrap sx={{ color: tokens.textoSecundario }}>
                                ↳ {formatarDetalhe(detalhe)}
                            </Typography>
                        ))}
                    </Box>
                )}

                {/* Rodapé: estação principal + tempo + botão disparar tudo do grupo */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, pb: 1 }}>
                    <Chip
                        label={nomeEstacao(itemPrincipal.estacaoKdsUuid)}
                        size="small"
                        sx={{ background: corEstacao(itemPrincipal.estacaoKdsUuid), color: 'white', fontSize: '0.7rem', minWidth: 70, flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    {itemPrincipal.tempoEstimadoSegundos > 0 && (
                        <Typography fontSize="0.78rem" sx={{ flexShrink: 0, color: tokens.textoSecundario }}>
                            {formatMin(itemPrincipal.tempoEstimadoSegundos)}
                        </Typography>
                    )}
                    {(() => {
                        const jaDisparado = itemPrincipal.status !== StatusItemKDS.AGUARDANDO_DISPARO || !!itemPrincipal.disparadoEm;
                        return !jaDisparado ? (
                            <Button variant="contained" color="warning" size="small"
                                startIcon={<PlayArrow sx={{ fontSize: 14 }} />}
                                onClick={() => dispararItemIndividual(itemPrincipal.uuid)}
                                sx={{
                                    textTransform: 'none', fontWeight: 'bold', fontSize: '0.75rem',
                                    py: 0.5, px: 1, flexShrink: 0,
                                }}
                            >
                                Disparar
                            </Button>
                        ) : (
                            <Chip
                                label="Disparado"
                                size="small"
                                icon={<CheckCircle sx={{ fontSize: '14px !important', color: `${tokens.palette.success.main} !important` }} />}
                                sx={{ background: tokens.tenue(tokens.palette.success.main, 0.14), color: tokens.palette.success.main, fontSize: '0.7rem', flexShrink: 0 }}
                            />
                        );
                    })()}
                </Box>
            </Box>
        );
    };

    const renderPedido = (pedido: IPedidoKDSExpedicao, showDispatch: boolean) => {
        const numeroPedido = pedido.numeroPedido ?? pedido.itens[0]?.numeroPedido;
        const primeiroItem = pedido.itens[0];
        const itensDisparar = pedido.itens.filter(i => i.status === StatusItemKDS.AGUARDANDO_DISPARO);
        const itensAtivos = pedido.itens.filter(i =>
            (i.status !== StatusItemKDS.AGUARDANDO_DISPARO || !showDispatch) &&
            i.status !== StatusItemKDS.CANCELADO &&
            i.status !== StatusItemKDS.LIBERADO
        );
        const statusKDS = calcularStatusKDS(pedido.itens);
        const isSelected = pedido.pedidoUuid === selectedPedidoUuid;

        const statusKDSConfig = {
            [StatusKDS.PARADO]: { label: 'Aguardando', color: tokens.textoFraco },
            [StatusKDS.EM_ANDAMENTO]: { label: 'Em preparo', color: tokens.palette.primary.main },
            [StatusKDS.CONCLUIDO]: { label: 'Pronto', color: tokens.palette.success.main },
        }[statusKDS];

        return (
            <Box key={pedido.pedidoUuid} sx={{
                background: tokens.card,
                border: `1px solid ${tokens.borda}`,
                borderLeft: `3px solid ${pedido.todosProntos
                    ? tokens.palette.success.main
                    : pedido.temDisparo
                        ? tokens.palette.warning.main
                        : pedido.emDisparo
                            ? tokens.palette.primary.main
                            : tokens.textoFraco}`,
                borderRadius: 2,
                overflow: 'hidden',
                ...(isSelected && { outline: `2px solid ${tokens.selecao}`, outlineOffset: '2px' }),
            }}>
                {/* Cabeçalho do pedido */}
                <Box sx={{
                    px: 2, py: 1,
                    background: pedido.todosProntos
                        ? tokens.tenue(tokens.palette.success.main, 0.1)
                        : pedido.temDisparo
                            ? tokens.tenue(tokens.palette.warning.main, 0.1)
                            : pedido.emDisparo
                                ? tokens.tenue(tokens.palette.primary.main, 0.1)
                                : tokens.superficie,
                    borderBottom: `1px solid ${tokens.borda}`,
                    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                }}>
                    <Box>
                        <Typography fontWeight={800} fontSize="1rem" sx={{ color: tokens.texto }}>
                            #{numeroPedido ?? pedido.pedidoUuid.slice(-6).toUpperCase()}
                        </Typography>
                        <Chip label={statusKDSConfig.label} size="small" sx={{ background: statusKDSConfig.color, color: 'white', fontSize: '0.6rem', height: '16px', mt: 0.25 }} />
                    </Box>
                    {primeiroItem && (
                        <KDSTimer
                            criadoEm={primeiroItem.criadoEm}
                            iniciadoEm={primeiroItem.iniciadoEm}
                            tempoEstimadoSegundos={pedido.tempoEstimadoTotalSegundos ?? 0}
                        />
                    )}
                    {pedido.tempoEstimadoTotalSegundos != null && pedido.tempoEstimadoTotalSegundos > 0 && (
                        <Chip
                            label={`${formatMin(pedido.tempoEstimadoTotalSegundos)} estimado`}
                            size="small"
                            sx={{ background: tokens.tenue(tokens.palette.primary.main, 0.12), color: tokens.palette.primary.main, fontSize: '0.72rem' }}
                        />
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        {/* Botão "disparar todos" no cabeçalho para pedidos aguardando */}
                        {showDispatch && itensDisparar.length > 1 && (
                            <Button variant="outlined" color="warning" size="small"
                                startIcon={<PlayArrow sx={{ fontSize: 14 }} />}
                                onClick={() => dispararPedido(pedido.pedidoUuid)}
                                sx={{
                                    textTransform: 'none', fontSize: '0.75rem', py: 0.25,
                                }}
                            >
                                Disparar todos
                            </Button>
                        )}
                        {/* Liberar */}
                        {pedido.todosProntos && (
                            <Button variant="contained" color="primary" size="small"
                                startIcon={<Send sx={{ fontSize: 14 }} />}
                                onClick={() => liberarPedido(pedido.pedidoUuid)}
                                sx={{
                                    textTransform: 'none', fontWeight: 'bold', fontSize: '0.75rem',
                                }}
                            >
                                Liberar
                            </Button>
                        )}
                    </Box>
                </Box>

                <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {/* Itens aguardando disparo — controle individual */}
                    {showDispatch && itensDisparar.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: 0.5, color: tokens.palette.warning.main }}>
                                AGUARDANDO DISPARO
                            </Typography>
                            {(() => {
                                const gruposDisparo = new Map<string, IItemPedidoKDS[]>();
                                for (const item of itensDisparar) {
                                    const grupo = gruposDisparo.get(item.produtoUuid) ?? [];
                                    gruposDisparo.set(item.produtoUuid, [...grupo, item]);
                                }
                                return Array.from(gruposDisparo.values()).map(grupo => renderGrupoDisparo(grupo));
                            })()}
                        </Box>
                    )}

                    {/* Itens ativos (em preparo / aguardando / prontos) — agrupados por produto */}
                    {itensAtivos.length > 0 && (() => {
                        const gruposMapa = new Map<string, { produtoDescricao: string; quantidade: number; itens: IItemPedidoKDS[] }>();
                        for (const item of itensAtivos) {
                            if (!gruposMapa.has(item.produtoUuid)) {
                                gruposMapa.set(item.produtoUuid, { produtoDescricao: item.produtoDescricao, quantidade: item.quantidade, itens: [] });
                            }
                            const g = gruposMapa.get(item.produtoUuid)!;
                            if (item.itemTipo === 'produto' && item.quantidade > 1) g.quantidade = item.quantidade;
                            g.itens.push(item);
                        }
                        const grupos = Array.from(gruposMapa.values());
                        const agrupado = grupos.some(g => g.itens.length > 1) || itensAtivos.some(i => !!i.processoDescricao);

                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {showDispatch && itensDisparar.length > 0 && (
                                    <Divider sx={{ borderColor: '#333', my: 0.25 }} />
                                )}
                                {agrupado ? grupos.map((grupo, gi) => {
                                    const normDesc = (s: string) => s.replace(/^adc:\s*/i, '').trim().toLowerCase();
                                    const produtoItens = grupo.itens.filter(i => i.itemTipo === 'produto');
                                    const complementoItens = grupo.itens.filter(i => i.itemTipo !== 'produto');
                                    const detalhesProduto = produtoItens.find(i => i.detalhesProduto?.length)?.detalhesProduto ?? [];
                                    const complementoNorms = new Set(complementoItens.map(c => normDesc(c.itemDescricao ?? '')));
                                    const detalhesExtras = detalhesProduto.filter(d => !complementoNorms.has(normDesc(d.descricao)));
                                    const observacoesGrupo = grupo.itens.find(i => i.observacoes)?.observacoes;
                                    return (
                                    <Box key={gi}>
                                        {/* Cabeçalho do produto */}
                                        <Typography fontSize="0.8rem" fontWeight={700} sx={{ mb: 0.25, color: tokens.textoSecundario }}>
                                            {grupo.quantidade}x {grupo.produtoDescricao}
                                        </Typography>
                                        {observacoesGrupo && (
                                            <Typography fontSize="0.75rem" sx={{ pl: 1, color: tokens.palette.warning.main, fontWeight: 600 }}>⚠ {observacoesGrupo}</Typography>
                                        )}
                                        {/* Detalhes sem estação própria (borda/massa/sabor/adicionais sem station) */}
                                        {detalhesExtras.map((detalhe, i) => (
                                            <Typography key={i} fontSize="0.75rem" sx={{ pl: 1, color: tokens.textoSecundario }}>
                                                ↳ {formatarDetalhe(detalhe)}
                                            </Typography>
                                        ))}
                                        {/* Processos */}
                                        {produtoItens.map(item => (
                                            <Box key={item.uuid} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: 1 }}>
                                                <IconeStatus status={item.status} />
                                                <Typography fontSize="0.85rem" fontWeight={600} sx={{ flex: 1, color: tokens.texto }}>
                                                    ↳ {item.quantidade}x {item.processoDescricao ?? item.produtoDescricao}
                                                </Typography>
                                                <Chip
                                                    label={nomeEstacao(item.estacaoKdsUuid)}
                                                    size="small"
                                                    sx={{ background: corEstacao(item.estacaoKdsUuid), color: 'white', fontSize: '0.68rem', height: 20, flexShrink: 0 }}
                                                />
                                                {(item.status === StatusItemKDS.PRONTO || item.status === StatusItemKDS.EM_PREPARO) && (
                                                    <Tooltip title="Desfazer">
                                                        <IconButton size="small" onClick={() => reverterItem(item.uuid)} sx={{ color: tokens.textoFraco, p: 0.25 }}>
                                                            <Undo sx={{ fontSize: '0.85rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        ))}
                                        {/* Complementos com estação própria */}
                                        {complementoItens.map(item => (
                                            <Box key={item.uuid} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pl: 1 }}>
                                                <IconeStatus status={item.status} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography fontSize="0.85rem" fontWeight={600} sx={{ color: tokens.texto }}>
                                                        ↳ {item.quantidade}x {item.itemDescricao ? formatarDetalhe({ tipo: item.itemTipo ?? 'item', descricao: item.itemDescricao }) : item.produtoDescricao}
                                                    </Typography>
                                                    {item.processoDescricao && (
                                                        <Typography fontSize="0.78rem" sx={{ pl: 1, color: tokens.textoSecundario }}>
                                                            ↳ {item.processoDescricao}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Chip
                                                    label={nomeEstacao(item.estacaoKdsUuid)}
                                                    size="small"
                                                    sx={{ background: corEstacao(item.estacaoKdsUuid), color: 'white', fontSize: '0.68rem', height: 20, flexShrink: 0 }}
                                                />
                                                {(item.status === StatusItemKDS.PRONTO || item.status === StatusItemKDS.EM_PREPARO) && (
                                                    <Tooltip title="Desfazer">
                                                        <IconButton size="small" onClick={() => reverterItem(item.uuid)} sx={{ color: tokens.textoFraco, p: 0.25 }}>
                                                            <Undo sx={{ fontSize: '0.85rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        ))}
                                        {gi < grupos.length - 1 && <Divider sx={{ borderColor: tokens.borda, mt: 0.5 }} />}
                                    </Box>
                                    );
                                }) : itensAtivos.map(item => (
                                    <Box key={item.uuid} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <IconeStatus status={item.status} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography fontSize="0.85rem" fontWeight={600} sx={{ color: tokens.texto }}>
                                                {item.quantidade}x {item.produtoDescricao}
                                            </Typography>
                                            {item.detalhesProduto?.map((detalhe, i) => (
                                                <Typography key={i} fontSize="0.75rem" noWrap sx={{ color: tokens.textoSecundario }}>
                                                    ↳ {formatarDetalhe(detalhe)}
                                                </Typography>
                                            ))}
                                            {item.observacoes && (
                                                <Typography sx={{ color: tokens.palette.warning.main, fontWeight: 600 }} fontSize="0.75rem">⚠ {item.observacoes}</Typography>
                                            )}
                                        </Box>
                                        <Chip
                                            label={nomeEstacao(item.estacaoKdsUuid)}
                                            size="small"
                                            sx={{ background: corEstacao(item.estacaoKdsUuid), color: 'white', fontSize: '0.68rem', height: 20, flexShrink: 0 }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        );
                    })()}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100vw', height: '100vh', background: tokens.painel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ background: tokens.palette.secondary.main, px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Voltar para configurações">
                        <IconButton size="small" onClick={() => navigate('/cozinha/configuracao')} sx={{ color: 'white' }}>
                            <ArrowBack fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" fontWeight="bold" color="white">Expedição</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    {aguardandoDisparo.length > 0 && (
                        <Typography color="common.white" fontSize="0.9rem" fontWeight="bold">
                            {aguardandoDisparo.length} aguardando disparo
                        </Typography>
                    )}
                    {emDisparo.length > 0 && (
                        <Typography color="common.white" fontSize="0.9rem">{emDisparo.length} em disparo</Typography>
                    )}
                    <Typography color="white" fontSize="0.9rem">{emAndamento.length} em andamento</Typography>
                    <Typography color="white" fontSize="0.9rem">{prontos.length} prontos</Typography>
                    {loading && <CircularProgress size={20} sx={{ color: 'white' }} />}
                </Box>
            </Box>

            {/* Conteúdo */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2, alignContent: 'flex-start', alignItems: 'start' }}>
                {pedidos.length === 0 && !loading && (
                    <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                        <Typography color="text.secondary" variant="h5">Nenhum pedido aguardando</Typography>
                    </Box>
                )}

                {/* Pedidos aguardando disparo: aparecem primeiro, ordem de chegada */}
                {aguardandoDisparo.map(p => renderPedido(p, true))}

                {/* Em disparo (auto-fire aguardando o fire time) */}
                {emDisparo.map(p => renderPedido(p, false))}

                {/* Prontos para liberar */}
                {prontos.map(p => renderPedido(p, false))}

                {/* Em andamento */}
                {emAndamento.map(p => renderPedido(p, false))}
            </Box>
        </Box>
    );
};
